import { type GeoLocation } from '@smg-real-estate/geo-libs';

import { trackEvent } from '@/helpers/GTM/GTMHelper';
import {
  EventLabel,
  InteractionEvent,
  InteractionGoal,
  PageType,
  SortingOptionEventLabel,
} from '@/helpers/GTM/types';
import { ListingType, ListingTypeForTracking, SortingOption, type Agency } from '@/types';
import { AgentService, AgentSpecialization, type AgentProfile } from '@/types/agentProfile';

/** SPA-session flag: resets only on full page load / hard refresh. */
let hasSubmittedAgentDirectorySearch = false;

/** Test helper — not for production call sites. */
export const resetAgentDirectorySearchSubmitState = (): void => {
  hasSubmittedAgentDirectorySearch = false;
};

const AGENT_SPECIALISATION_FILTER_LABELS: Record<string, string> = {
  [AgentSpecialization.InitialMarketing]: 'initial_marketing',
  [AgentSpecialization.InvestmentProperties]: 'investment_properties',
  [AgentService.GeneralContracting]: 'new_construction',
  [AgentSpecialization.LuxuryProperties]: 'luxury_properties',
  [AgentSpecialization.FirstRentals]: 'first_rentals',
  [AgentSpecialization.CommercialOffice]: 'commercial_and_office',
};

export const getAgentSpecialisationFilterLabel = (chipKey: string): string | undefined =>
  AGENT_SPECIALISATION_FILTER_LABELS[chipKey];

interface AgencyDirectoryPageViewTrackingParams {
  agencies: Agency[];
  filterLocations: string[];
  totalAgenciesCount: number;
  pageCount: number;
  currentPage: number;
  language: string;
  listingType: ListingType;
  sortingOption: SortingOption;
}

export const getSortingLabel = (sortingOption: SortingOption): SortingOptionEventLabel => {
  const sortingLabels: Record<SortingOption, SortingOptionEventLabel> = {
    [SortingOption.ALPHABETICALLY]: SortingOptionEventLabel.ALPHABET_ASC,
    [SortingOption.PROPERTIES_FOR_RENT]: SortingOptionEventLabel.TOTALRENTADS_DESC,
    [SortingOption.PROPERTIES_FOR_SALE]: SortingOptionEventLabel.TOTALSALEADS_DESC,
    [SortingOption.DEFAULT]: SortingOptionEventLabel.COMPLETENESS_DESC,
  };

  return sortingLabels[sortingOption];
};

export const trackAgencyDirectoryPageView = ({
  agencies,
  filterLocations,
  totalAgenciesCount,
  pageCount,
  currentPage,
  language,
  listingType,
  sortingOption,
}: AgencyDirectoryPageViewTrackingParams): void => {
  trackEvent(
    InteractionEvent.LOAD,
    buildAgencyDirectoryPageViewPayload({
      agencies,
      filterLocations,
      totalAgenciesCount,
      pageCount,
      currentPage,
      language,
      listingType,
      sortingOption,
    }),
  );
};

export const buildAgencyDirectoryPageViewPayload = ({
  agencies,
  filterLocations,
  totalAgenciesCount,
  pageCount,
  currentPage,
  language,
  listingType,
  sortingOption,
}: AgencyDirectoryPageViewTrackingParams) => {
  return {
    event: InteractionEvent.LOAD,
    h_page_type_110: PageType.AGENCY_DIRECTORY,
    h_page_subtype_112: ListingTypeForTracking[listingType],
    h_content_language_116: language,
    h_search_term_geo_080:
      filterLocations.length > 0
        ? filterLocations.map((item) => (item.startsWith('geo-') ? item : `geo-${item}`)).join(',')
        : undefined,
    h_number_of_search_terms_entered_278: filterLocations.length,
    h_search_offer_type_083: ListingTypeForTracking[listingType],
    h_list_name_086: 'agency_directory_search_result',
    h_srp_items_loaded_078: agencies.map((agency: Agency) => agency.customerId),
    h_search_results_loaded_279: agencies.length,
    h_search_results_total_280: totalAgenciesCount,
    h_search_results_pages_all_287: pageCount,
    h_search_results_page_current_288: currentPage,
    h_search_sorted_details_073: getSortingLabel(sortingOption),
  };
};

interface SearchButtonSubmitTrackingParams {
  selectedLocations: GeoLocation[];
  listingType: ListingType;
}
export const trackSearchButtonSubmit = ({
  selectedLocations,
  listingType,
}: SearchButtonSubmitTrackingParams): void => {
  trackEvent(InteractionEvent.SUBMIT_SEARCH, {
    event: InteractionEvent.SUBMIT_SEARCH,
    h_event_category_006: InteractionEvent.SUBMIT_SEARCH,
    h_event_action_007:
      selectedLocations.length > 0 ? 'agency_directory_changeSearch' : 'agency_directory_search',
    h_event_label_008: selectedLocations.map((location) => location.id).join(','),
    h_search_term_geo_080: selectedLocations.map((location) => location.id).join(','),
    h_search_offer_type_083: ListingTypeForTracking[listingType],
    h_number_of_search_terms_entered_278: selectedLocations.length,
  });
};

export const trackFeaturedAgencyCardClick = ({ customerId }: Agency, position: number): void => {
  trackEvent(InteractionEvent.LIST_CLICKTHROUGH, {
    event: InteractionEvent.LIST_CLICKTHROUGH,
    h_event_category_006: 'list_clickthrough',
    h_event_action_007: 'agency_recommendation',
    h_event_label_008: customerId,
    h_list_name_086: 'agency_recommendation',
    h_list_item_clicked_id_085: customerId,
    h_position_on_list_overall_283: position,
  });
};

interface SearchResultsAgencyCardClickTrackingParams {
  clickedSearchResultsAgencyCard: Agency;
  position: number;
}
export const trackSearchResultsAgencyCardClick = ({
  clickedSearchResultsAgencyCard,
  position,
}: SearchResultsAgencyCardClickTrackingParams): void => {
  trackEvent(InteractionEvent.LIST_CLICKTHROUGH, {
    event: InteractionEvent.LIST_CLICKTHROUGH,
    h_event_category_006: 'list_clickthrough',
    h_event_action_007: 'agency_directory_search_result',
    h_event_label_008: clickedSearchResultsAgencyCard.customerId,
    h_list_name_086: 'agency_directory_search_result',
    h_list_item_clicked_id_085: clickedSearchResultsAgencyCard.customerId,
    h_position_on_list_overall_283: position,
  });
};

export const trackPaginationClick = (operation: string): void => {
  trackEvent(InteractionEvent.GENERIC_INTERACTION, {
    event: 'generic_interaction',
    h_event_category_006: 'generic_interaction',
    h_event_action_007: 'sort',
    h_event_label_008: operation,
  });
};

export const trackFavouritesNavigation = (language: string): void => {
  trackEvent(InteractionEvent.HEADER_INTERACTION, {
    event: InteractionEvent.HEADER_INTERACTION,
    h_event_category_006: InteractionEvent.HEADER_INTERACTION,
    h_event_action_007: InteractionGoal.OPEN_FAVOURITES,
    h_event_label_008: EventLabel.TAB_NAVIGATION,
    h_content_language_116: language,
  });
};

export const trackSortingOptionChange = (sortingOption: SortingOption): void => {
  trackEvent(InteractionEvent.SEARCH_SORT, {
    event: InteractionEvent.SEARCH_SORT,
    h_event_category_006: 'search_sort',
    h_event_action_007: 'sort',
    h_event_label_008: getSortingLabel(sortingOption),
  });
};

interface AgentDirectoryPageViewTrackingParams {
  agents: AgentProfile[];
  filterLocations: string[];
  totalAgentsCount: number;
  pageCount: number;
  currentPage: number;
  language: string;
}

export const buildAgentDirectoryPageViewPayload = ({
  agents,
  filterLocations,
  totalAgentsCount,
  pageCount,
  currentPage,
  language,
}: AgentDirectoryPageViewTrackingParams) => {
  return {
    event: InteractionEvent.LOAD,
    h_page_type_110: PageType.AGENT_DIRECTORY,
    h_page_subtype_112: undefined,
    h_content_language_116: language,
    h_search_term_geo_080:
      filterLocations.length > 0
        ? filterLocations.map((item) => (item.startsWith('geo-') ? item : `geo-${item}`)).join(',')
        : undefined,
    h_number_of_search_terms_entered_278: filterLocations.length,
    h_list_name_086: 'agent_directory_search_results',
    h_srp_items_loaded_078: agents.map((agent) => String(agent.agentId)),
    h_search_results_loaded_279: agents.length,
    h_search_results_total_280: totalAgentsCount,
    h_search_results_pages_all_287: pageCount,
    h_search_results_page_current_288: currentPage,
  };
};

export const trackAgentDirectoryPageView = (params: AgentDirectoryPageViewTrackingParams): void => {
  trackEvent(InteractionEvent.LOAD, buildAgentDirectoryPageViewPayload(params));
};

export const trackDirectoryModeToggle = (mode: 'agent' | 'agency'): void => {
  trackEvent(InteractionEvent.BUTTON_CLICK, {
    event: InteractionEvent.BUTTON_CLICK,
    h_event_action_007: `switch_to_${mode}_directory`,
    h_event_label_008: undefined,
  });
};

export const trackAgentSearchButtonSubmit = ({
  selectedLocations,
}: {
  selectedLocations: GeoLocation[];
}): void => {
  const action = hasSubmittedAgentDirectorySearch
    ? 'agent_directory_changeSearch'
    : 'agent_directory_search';
  hasSubmittedAgentDirectorySearch = true;

  const locationIds = selectedLocations.map((location) => location.id).join(',');

  trackEvent(InteractionEvent.SUBMIT_SEARCH, {
    event: InteractionEvent.SUBMIT_SEARCH,
    h_event_action_007: action,
    h_event_label_008: locationIds,
    h_search_term_geo_080: locationIds,
    h_number_of_search_terms_entered_278: selectedLocations.length,
  });
};

export const trackAgentSpecialisationFilterApply = (chipKey: string): void => {
  const label = getAgentSpecialisationFilterLabel(chipKey);
  if (!label) {
    return;
  }

  trackEvent(InteractionEvent.BUTTON_CLICK, {
    event: InteractionEvent.BUTTON_CLICK,
    h_event_action_007: 'apply_agent_specialisation_filter',
    h_event_label_008: label,
  });
};

interface SearchResultsAgentCardClickTrackingParams {
  clickedAgent: AgentProfile;
  position: number;
}

export const trackSearchResultsAgentCardClick = ({
  clickedAgent,
  position,
}: SearchResultsAgentCardClickTrackingParams): void => {
  const agentId = String(clickedAgent.agentId);

  trackEvent(InteractionEvent.LIST_CLICKTHROUGH, {
    event: InteractionEvent.LIST_CLICKTHROUGH,
    h_event_action_007: 'view_agent_profile_page',
    h_event_label_008: agentId,
    h_list_name_086: 'agent_directory_search_result',
    h_list_item_clicked_id_085: agentId,
    h_position_on_list_overall_283: position,
  });
};
